import { DROMO_DATE_FORMAT } from "integrations/dromo/dromoFormats";

// Helper function to get the proper field name based on debtor number
export function getDebtorFieldKey(fieldName, debtorNumber) {
  // NOTE: We initially implemented this Debtor creation to only take a single Debtor.
  // Since existing clients already have column mappings tied to these initial Debtor fields,
  // we decided to keep the first debtor the same. We are not adding the `debtor_${debtorNumber}_`
  // prefix to the first debtor because if we change the field key, agencies will have
  // to manually re-perform column mapping on the file import when they upload a data import
  // file for each Dromo account import type
  // https://developer.dromo.io/faqs/#can-you-remember-mappings-from-one-import-to-the-next
  // https://developer.dromo.io/settings/#automapheaders
  return debtorNumber > 1 ? `debtor_${debtorNumber}_${fieldName}` : fieldName;
}

// Base required together field sets (without debtor prefixes)
const baseDebtorRequiredTogetherFields = [
  [
    "debtor_address_1",
    "debtor_address_city",
    "debtor_address_state",
    "debtor_address_zip_code",
    "debtor_address_type",
  ],
  [
    "debtor_address_2_1",
    "debtor_address_2_city",
    "debtor_address_2_state",
    "debtor_address_2_zip_code",
    "debtor_address_2_type",
  ],
  [
    "debtor_address_3_1",
    "debtor_address_3_city",
    "debtor_address_3_state",
    "debtor_address_3_zip_code",
    "debtor_address_3_type",
  ],
  ["debtor_email_1_address", "debtor_email_1_type", "debtor_email_1_consent_status"],
  ["debtor_email_2_address", "debtor_email_2_type", "debtor_email_2_consent_status"],
  ["debtor_email_3_address", "debtor_email_3_type", "debtor_email_3_consent_status"],
];

// Function to generate required together fields for all debtors
export function getDebtorRequiredTogetherFields(numberOfDebtors) {
  const result = [];
  for (let debtorNumber = 1; debtorNumber <= numberOfDebtors; debtorNumber++) {
    baseDebtorRequiredTogetherFields.forEach((fieldGroup) => {
      result.push(
        new Set(fieldGroup.map((fieldName) => getDebtorFieldKey(fieldName, debtorNumber))),
      );
    });
  }
  return result;
}

export const consentStatusType = [
  { label: "Unknown", value: "unknown" },
  { label: "Consent", value: "consent" },
  { label: "No Consent", value: "no_consent" },
];

export const usStates = [
  { label: "AL", value: "AL" },
  { label: "AK", value: "AK" },
  { label: "AZ", value: "AZ" },
  { label: "AE", value: "AE" },
  { label: "AP", value: "AP" },
  { label: "AR", value: "AR" },
  { label: "CA", value: "CA" },
  { label: "CO", value: "CO" },
  { label: "CT", value: "CT" },
  { label: "DC", value: "DC" },
  { label: "DE", value: "DE" },
  { label: "FL", value: "FL" },
  { label: "GA", value: "GA" },
  { label: "GU", value: "GU" },
  { label: "HI", value: "HI" },
  { label: "ID", value: "ID" },
  { label: "IL", value: "IL" },
  { label: "IN", value: "IN" },
  { label: "IA", value: "IA" },
  { label: "KS", value: "KS" },
  { label: "KY", value: "KY" },
  { label: "LA", value: "LA" },
  { label: "ME", value: "ME" },
  { label: "MD", value: "MD" },
  { label: "MA", value: "MA" },
  { label: "MI", value: "MI" },
  { label: "MN", value: "MN" },
  { label: "MS", value: "MS" },
  { label: "MO", value: "MO" },
  { label: "MT", value: "MT" },
  { label: "NE", value: "NE" },
  { label: "NV", value: "NV" },
  { label: "NH", value: "NH" },
  { label: "NJ", value: "NJ" },
  { label: "NM", value: "NM" },
  { label: "NY", value: "NY" },
  { label: "NC", value: "NC" },
  { label: "ND", value: "ND" },
  { label: "MP", value: "MP" },
  { label: "OH", value: "OH" },
  { label: "OK", value: "OK" },
  { label: "OR", value: "OR" },
  { label: "PA", value: "PA" },
  { label: "PR", value: "PR" },
  { label: "RI", value: "RI" },
  { label: "SC", value: "SC" },
  { label: "SD", value: "SD" },
  { label: "TN", value: "TN" },
  { label: "TX", value: "TX" },
  { label: "UT", value: "UT" },
  { label: "VT", value: "VT" },
  { label: "VA", value: "VA" },
  { label: "VI", value: "VI" },
  { label: "WA", value: "WA" },
  { label: "WV", value: "WV" },
  { label: "WI", value: "WI" },
  { label: "WY", value: "WY" },
];

export const phoneType = [
  { label: "Work", value: "work" },
  { label: "Home", value: "home" },
  { label: "Cell", value: "cell" },
  { label: "Fax", value: "fax" },
  { label: "Intelligent Contacts", value: "intelligent_contacts" },
  { label: "Other", value: "other" },
  { label: "Invalid", value: "invalid" },
];

export const phoneStatus = [
  { label: "None", value: "" },
  { label: "Active", value: "active" },
  { label: "Disconnected", value: "disconnected" },
  { label: "Questionable", value: "questionable" },
  { label: "Request Callback Consent", value: "request_callback_consent" },
  { label: "Wrong Number", value: "wrong_number" },
  { label: "Do Not Call", value: "do_not_call" },
];

export const dataSourceType = [
  { label: "Data Migration Legacy Software", value: "data_migration_legacy_software" },
  { label: "Manual Upload", value: "manual_upload" },
  { label: "Skip Trace", value: "skip_trace" },
  { label: "Third Party Upload", value: "third_party_upload" },
  { label: "Bulk Upload", value: "bulk_upload" },
];

export const employmentTypeOptions = [
  { label: "Full-time", value: "full_time" },
  { label: "Part-time", value: "part_time" },
  { label: "Contract", value: "contract" },
  { label: "Self-employed", value: "self_employed" },
  { label: "Unemployed", value: "unemployed" },
  { label: "Retired", value: "retired" },
  { label: "Student", value: "student" },
  { label: "Other", value: "other" },
];

export const emailType = [
  { label: "Personal", value: "personal" },
  { label: "Work", value: "work" },
  { label: "Intelligent Contacts", value: "intelligent_contacts" },
  { label: "Other", value: "other" },
  { label: "Invalid", value: "invalid" },
];

export const emailStatusType = [
  { label: "None", value: "" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Undeliverable", value: "undeliverable" },
];

export const addressType = [
  { label: "Mail", value: "mail" },
  { label: "Home", value: "home" },
  { label: "Business", value: "business" },
  { label: "Mail Return", value: "mail_return" },
  { label: "Billing", value: "billing" },
  { label: "Service", value: "service" },
  { label: "Intelligent Contacts", value: "intelligent_contacts" },
  { label: "Other", value: "other" },
  { label: "Invalid", value: "invalid" },
];

const consumerContactRelationType = [
  { label: "Spouse", value: "spouse" },
  { label: "Partner", value: "partner" },
  { label: "Sibling", value: "sibling" },
  { label: "Parent", value: "parent" },
  { label: "Guardian", value: "guardian" },
  { label: "Family", value: "family" },
  { label: "Attorney", value: "attorney" },
  { label: "Employer", value: "employer" },
  { label: "Friend", value: "friend" },
  { label: "Neighbor", value: "neighbor" },
  { label: "Other", value: "other" },
];

const commercialContactRelationType = [
  { label: "Attorney", value: "attorney" },
  { label: "Owner", value: "owner" },
  { label: "Accountant", value: "accountant" },
  { label: "Manager", value: "manager" },
  { label: "Other", value: "other" },
];

export const militaryStatusOptions = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Future Active", value: "FUTURE_ACTIVE" },
  { label: "Non-Active", value: "NON_ACTIVE" },
];

export const bankruptcyChapterOptions = [
  {
    label: "Chapter 7",
    value: "Chapter 7",
  },
  {
    label: "Chapter 7A",
    value: "Chapter 7A",
  },
  {
    label: "Chapter 9",
    value: "Chapter 9",
  },
  {
    label: "Chapter 11",
    value: "Chapter 11",
  },
  {
    label: "Chapter 12",
    value: "Chapter 12",
  },
  {
    label: "Chapter 13",
    value: "Chapter 13",
  },
  {
    label: "Chapter 15",
    value: "Chapter 15",
  },
];

export const debtorMilitaryFields = [
  {
    key: "debtor_military_status",
    label: "Debtor Military Status",
    type: "select",
    selectOptions: militaryStatusOptions,
  },
  {
    key: "debtor_military_service_branch",
    label: "Debtor Military Service Branch",
    type: "string",
  },
  {
    key: "debtor_military_active_duty_start_date",
    label: "Debtor Military Active Duty Start Date",
    type: DROMO_DATE_FORMAT,
  },
  {
    key: "debtor_military_active_duty_end_date",
    label: "Debtor Military Active Duty End Date",
    type: DROMO_DATE_FORMAT,
  },
  {
    key: "debtor_military_scrub_date",
    label: "Debtor Military Verification Date",
    type: DROMO_DATE_FORMAT,
  },
  {
    key: "debtor_military_certificate_id",
    label: "Debtor Military SCRA Certificate ID",
    type: "string",
  },
  {
    key: "debtor_military_notes",
    label: "Debtor Military Notes",
    type: "string",
  },
];

export const debtorEmployerFields = [
  {
    key: "debtor_employer_name",
    label: "Debtor Employer Name",
    type: "string",
    validators: [
      {
        validate: "require_with",
        fields: [
          "debtor_employer_address_1",
          "debtor_employer_address_2",
          "debtor_employer_address_city",
          "debtor_employer_address_state",
          "debtor_employer_address_zip_code",
          "debtor_employer_phone_number",
          "debtor_employer_job_title",
          "debtor_employer_salary",
          "debtor_employer_email_address",
          "debtor_employer_start_date",
          "debtor_employer_end_date",
          "debtor_employer_employment_type",
          "debtor_employer_notes",
        ],
      },
    ],
  },
  {
    key: "debtor_employer_address_1",
    label: "Debtor Employer Address Line 1",
    type: "string",
  },
  {
    key: "debtor_employer_address_2",
    label: "Debtor Employer Address Line 2",
    type: "string",
  },
  {
    key: "debtor_employer_address_city",
    label: "Debtor Employer Address City",
    type: "string",
  },
  {
    key: "debtor_employer_address_state",
    label: "Debtor Employer Address State",
    type: "select",
    selectOptions: usStates,
  },
  {
    key: "debtor_employer_address_zip_code",
    label: "Debtor Employer Address Zip Code",
    type: "string",
  },
  {
    key: "debtor_employer_phone_number",
    label: "Debtor Employer Phone Number",
    type: "string",
  },
  {
    key: "debtor_employer_is_primary",
    label: "Debtor Employer Is Primary",
    type: "checkbox",
  },
  {
    key: "debtor_employer_job_title",
    label: "Debtor Employer Job Title",
    type: "string",
  },
  {
    key: "debtor_employer_salary",
    label: "Debtor Employer Salary",
    type: "number",
    required: false,
  },
  {
    key: "debtor_employer_email_address",
    label: "Debtor Employer Email",
    type: "string",
    required: false,
  },
  {
    key: "debtor_employer_start_date",
    label: "Debtor Employer Start Date",
    type: DROMO_DATE_FORMAT,
  },
  {
    key: "debtor_employer_end_date",
    label: "Debtor Employer End Date",
    type: DROMO_DATE_FORMAT,
  },
  {
    key: "debtor_employer_employment_type",
    label: "Debtor Employer Employment Type",
    type: "select",
    selectOptions: employmentTypeOptions,
  },
  {
    key: "debtor_employer_notes",
    label: "Debtor Employer Notes",
    type: "string",
  },
  {
    key: "debtor_employer_data_source",
    label: "Debtor Employer Data Source",
    type: "select",
    selectOptions: dataSourceType,
  },
];

export const debtorPhoneFields = [
  {
    key: "debtor_phone_number_1",
    label: "Debtor Phone Number 1",
    type: "string",
  },
  {
    key: "debtor_phone_type_1",
    label: "Debtor Phone Type 1",
    type: "select",
    selectOptions: phoneType,
    validators: [
      {
        validate: "require_with",
        fields: ["debtor_phone_number_1"],
      },
    ],
  },
  {
    key: "debtor_phone_is_primary_1",
    label: "Debtor Phone Is Primary 1",
    type: "checkbox",
  },
  {
    key: "debtor_phone_ai_permission_to_call_status_1",
    label: "Debtor Phone AI Permission to Call Status 1",
    type: "select",
    selectOptions: consentStatusType,
  },
  {
    key: "debtor_phone_permission_to_call_status_1",
    label: "Debtor Phone Permission to Call Status 1",
    type: "select",
    selectOptions: consentStatusType,
  },
  {
    key: "debtor_phone_permission_to_text_status_1",
    label: "Debtor Phone Permission to Text Status 1",
    type: "select",
    selectOptions: consentStatusType,
  },
  {
    key: "debtor_phone_status_1",
    label: "Debtor Phone Status 1",
    type: "select",
    selectOptions: phoneStatus,
  },
  {
    key: "debtor_phone_extension_1",
    label: "Phone 1 Extension",
    type: "string",
  },
  {
    key: "debtor_phone_notes_1",
    label: "Phone 1 Notes",
    type: "string",
  },
  {
    key: "debtor_phone_data_source_1",
    label: "Phone 1 Source",
    type: "select",
    selectOptions: dataSourceType,
  },
  {
    key: "debtor_phone_number_2",
    label: "Debtor Phone Number 2",
    type: "string",
  },
  {
    key: "debtor_phone_type_2",
    label: "Debtor Phone Type 2",
    type: "select",
    selectOptions: phoneType,
    validators: [
      {
        validate: "require_with",
        fields: ["debtor_phone_number_2"],
      },
    ],
  },
  {
    key: "debtor_phone_is_primary_2",
    label: "Debtor Phone Is Primary 2",
    type: "checkbox",
  },
  {
    key: "debtor_phone_ai_permission_to_call_status_2",
    label: "Debtor Phone AI Permission to Call Status 2",
    type: "select",
    selectOptions: consentStatusType,
  },
  {
    key: "debtor_phone_permission_to_call_status_2",
    label: "Debtor Phone Permission to Call Status 2",
    type: "select",
    selectOptions: consentStatusType,
  },
  {
    key: "debtor_phone_permission_to_text_status_2",
    label: "Debtor Phone Permission to Text Status 2",
    type: "select",
    selectOptions: consentStatusType,
  },
  {
    key: "debtor_phone_status_2",
    label: "Debtor Phone Status 2",
    type: "select",
    selectOptions: phoneStatus,
  },
  {
    key: "debtor_phone_extension_2",
    label: "Phone 2 Extension",
    type: "string",
  },
  {
    key: "debtor_phone_notes_2",
    label: "Phone 2 Notes",
    type: "string",
  },
  {
    key: "debtor_phone_data_source_2",
    label: "Phone 2 Source",
    type: "select",
    selectOptions: dataSourceType,
  },
  {
    key: "debtor_phone_number_3",
    label: "Debtor Phone Number 3",
    type: "string",
  },
  {
    key: "debtor_phone_type_3",
    label: "Debtor Phone Type 3",
    type: "select",
    selectOptions: phoneType,
    validators: [
      {
        validate: "require_with",
        fields: ["debtor_phone_number_3"],
      },
    ],
  },
  {
    key: "debtor_phone_is_primary_3",
    label: "Debtor Phone Is Primary 3",
    type: "checkbox",
  },
  {
    key: "debtor_phone_ai_permission_to_call_status_3",
    label: "Debtor Phone AI Permission to Call Status 3",
    type: "select",
    selectOptions: consentStatusType,
  },
  {
    key: "debtor_phone_permission_to_call_status_3",
    label: "Debtor Phone Permission to Call Status 3",
    type: "select",
    selectOptions: consentStatusType,
  },
  {
    key: "debtor_phone_permission_to_text_status_3",
    label: "Debtor Phone Permission to Text Status 3",
    type: "select",
    selectOptions: consentStatusType,
  },
  {
    key: "debtor_phone_status_3",
    label: "Debtor Phone Status 3",
    type: "select",
    selectOptions: phoneStatus,
  },
  {
    key: "debtor_phone_extension_3",
    label: "Phone 3 Extension",
    type: "string",
  },
  {
    key: "debtor_phone_notes_3",
    label: "Phone 3 Notes",
    type: "string",
  },
  {
    key: "debtor_phone_data_source_3",
    label: "Phone 3 Source",
    type: "select",
    selectOptions: dataSourceType,
  },
];

export const debtorEmailFields = [
  {
    key: "debtor_email_1_address",
    label: "Debtor Email 1 Address",
    type: "email",
    validators: [
      {
        validate: "require_with",
        fields: ["debtor_email_1_status", "debtor_email_1_consent_date"],
      },
    ],
  },
  {
    key: "debtor_email_1_type",
    label: "Debtor Email 1 Type",
    type: "select",
    selectOptions: emailType,
  },
  {
    key: "debtor_email_1_status",
    label: "Debtor Email 1 Status",
    type: "select",
    selectOptions: emailStatusType,
  },
  {
    key: "debtor_email_1_consent_status",
    label: "Debtor Email 1 Consent Status",
    type: "select",
    selectOptions: consentStatusType,
  },
  {
    key: "debtor_email_1_consent_date",
    label: "Debtor Email 1 Consent Date",
    type: DROMO_DATE_FORMAT,
  },
  {
    key: "debtor_email_1_is_primary",
    label: "Debtor Email 1 Is Primary",
    type: "checkbox",
  },
  {
    key: "debtor_email_1_notes",
    label: "Email 1 Notes",
    type: "string",
  },
  {
    key: "debtor_email_1_data_source",
    label: "Email 1 Source",
    type: "select",
    selectOptions: dataSourceType,
  },
  {
    key: "debtor_email_2_address",
    label: "Debtor Email 2 Address",
    type: "email",
    validators: [
      {
        validate: "require_with",
        fields: ["debtor_email_2_status", "debtor_email_2_consent_date"],
      },
    ],
  },
  {
    key: "debtor_email_2_type",
    label: "Debtor Email 2 Type",
    type: "select",
    selectOptions: emailType,
  },
  {
    key: "debtor_email_2_status",
    label: "Debtor Email 2 Status",
    type: "select",
    selectOptions: emailStatusType,
  },
  {
    key: "debtor_email_2_consent_status",
    label: "Debtor Email 2 Consent Status",
    type: "select",
    selectOptions: consentStatusType,
  },
  {
    key: "debtor_email_2_consent_date",
    label: "Debtor Email 2 Consent Date",
    type: DROMO_DATE_FORMAT,
  },
  {
    key: "debtor_email_2_is_primary",
    label: "Debtor Email 2 Is Primary",
    type: "checkbox",
  },
  {
    key: "debtor_email_2_notes",
    label: "Email 2 Notes",
    type: "string",
  },
  {
    key: "debtor_email_2_data_source",
    label: "Email 2 Source",
    type: "select",
    selectOptions: dataSourceType,
  },
  {
    key: "debtor_email_3_address",
    label: "Debtor Email 3 Address",
    type: "email",
    validators: [
      {
        validate: "require_with",
        fields: ["debtor_email_3_status", "debtor_email_3_consent_date"],
      },
    ],
  },
  {
    key: "debtor_email_3_type",
    label: "Debtor Email 3 Type",
    type: "select",
    selectOptions: emailType,
  },
  {
    key: "debtor_email_3_status",
    label: "Debtor Email 3 Status",
    type: "select",
    selectOptions: emailStatusType,
  },
  {
    key: "debtor_email_3_consent_status",
    label: "Debtor Email 3 Consent Status",
    type: "select",
    selectOptions: consentStatusType,
  },
  {
    key: "debtor_email_3_consent_date",
    label: "Debtor Email 3 Consent Date",
    type: DROMO_DATE_FORMAT,
  },
  {
    key: "debtor_email_3_is_primary",
    label: "Debtor Email 3 Is Primary",
    type: "checkbox",
  },
  {
    key: "debtor_email_3_notes",
    label: "Email 3 Notes",
    type: "string",
  },
  {
    key: "debtor_email_3_data_source",
    label: "Email 3 Source",
    type: "select",
    selectOptions: dataSourceType,
  },
];

export const debtorBankruptcyFields = [
  {
    key: "debtor_bankruptcy_case_number",
    label: "Debtor Bankruptcy Case Number",
    type: "string",
  },
  {
    key: "debtor_bankruptcy_chapter",
    label: "Debtor Bankruptcy Chapter",
    type: "select",
    selectOptions: bankruptcyChapterOptions,
  },
  {
    key: "debtor_bankruptcy_filing_status",
    label: "Debtor Bankruptcy Filing Status",
    type: "string",
  },
  {
    key: "debtor_bankruptcy_filing_date",
    label: "Debtor Bankruptcy Filing Date",
    type: DROMO_DATE_FORMAT,
  },
  {
    key: "debtor_bankruptcy_dismissal_date",
    label: "Debtor Bankruptcy Dismissal Date",
    type: DROMO_DATE_FORMAT,
  },
  {
    key: "debtor_bankruptcy_discharge_date",
    label: "Debtor Bankruptcy Discharge Date",
    type: DROMO_DATE_FORMAT,
  },
  {
    key: "debtor_bankruptcy_notes",
    label: "Debtor Bankruptcy Notes",
    type: "string",
  },
];

export function getDebtorAddressFields(requiredFields) {
  const fields = [
    {
      key: "debtor_address_is_primary",
      label: "Is Address 1 Primary",
      type: "checkbox",
    },
    {
      key: "debtor_address_1",
      label: "Debtor Address Line 1",
      type: "string",
    },
    {
      key: "debtor_address_2",
      label: "Debtor Address Line 2",
      type: "string",
    },
    {
      key: "debtor_address_city",
      label: "Debtor Address City",
      type: "string",
    },
    {
      key: "debtor_address_state",
      label: "Debtor Address State",
      type: "select",
      selectOptions: usStates,
    },
    {
      key: "debtor_address_zip_code",
      label: "Debtor Address Zip Code",
      type: "string",
    },
    {
      key: "debtor_address_type",
      label: "Debtor Address Type",
      type: "select",
      selectOptions: addressType,
    },
    {
      key: "debtor_address_notes",
      label: "Primary Address Notes",
      type: "string",
    },
    {
      key: "debtor_address_data_source",
      label: "Primary Address Source",
      type: "select",
      selectOptions: dataSourceType,
    },
    {
      key: "debtor_address_2_is_primary",
      label: "Is Address 2 Primary",
      type: "checkbox",
    },
    {
      key: "debtor_address_2_1",
      label: "Debtor Address 2 Line 1",
      type: "string",
    },
    {
      key: "debtor_address_2_2",
      label: "Debtor Address 2 Line 2",
      type: "string",
    },
    {
      key: "debtor_address_2_city",
      label: "Debtor Address 2 City",
      type: "string",
    },
    {
      key: "debtor_address_2_state",
      label: "Debtor Address 2 State",
      type: "select",
      selectOptions: usStates,
    },
    {
      key: "debtor_address_2_zip_code",
      label: "Debtor Address 2 Zip Code",
      type: "string",
    },
    {
      key: "debtor_address_2_type",
      label: "Debtor Address 2 Type",
      type: "select",
      selectOptions: addressType,
    },
    {
      key: "debtor_address_2_notes",
      label: "Secondary Address Notes",
      type: "string",
    },
    {
      key: "debtor_address_2_data_source",
      label: "Secondary Address Source",
      type: "select",
      selectOptions: dataSourceType,
    },
    {
      key: "debtor_address_3_is_primary",
      label: "Is Address 3 Primary",
      type: "checkbox",
    },
    {
      key: "debtor_address_3_1",
      label: "Debtor Address 3 Line 1",
      type: "string",
    },
    {
      key: "debtor_address_3_2",
      label: "Debtor Address 3 Line 2",
      type: "string",
    },
    {
      key: "debtor_address_3_city",
      label: "Debtor Address 3 City",
      type: "string",
    },
    {
      key: "debtor_address_3_state",
      label: "Debtor Address 3 State",
      type: "select",
      selectOptions: usStates,
    },
    {
      key: "debtor_address_3_zip_code",
      label: "Debtor Address 3 Zip Code",
      type: "string",
    },
    {
      key: "debtor_address_3_type",
      label: "Debtor Address 3 Type",
      type: "select",
      selectOptions: addressType,
    },
    {
      key: "debtor_address_3_notes",
      label: "Third Address Notes",
      type: "string",
    },
    {
      key: "debtor_address_3_data_source",
      label: "Third Address Source",
      type: "select",
      selectOptions: dataSourceType,
    },
  ];
  if (requiredFields) {
    fields
      .filter((field) => requiredFields.includes(field.key))
      .forEach((field) => {
        field.validators = [{ validate: "required" }];
      });
  }
  return fields;
}

export function getCommercialDebtorFields(requiredFields) {
  const fields = [
    {
      key: "debtor_company_name",
      label: "Company Name",
      type: "string",
    },
    {
      key: "debtor_ein",
      label: "Company EIN",
      type: "string",
    },
  ];
  if (requiredFields) {
    fields
      .filter((field) => requiredFields.includes(field.key))
      .forEach((field) => {
        field.validators = [{ validate: "required" }];
      });
  }
  return fields;
}

export function getConsumerDebtorFields(requiredFields) {
  const fields = [
    {
      key: "debtor_first_name",
      label: "Debtor First Name",
      type: "string",
    },
    {
      key: "debtor_middle_name",
      label: "Debtor Middle Name",
      type: "string",
    },
    {
      key: "debtor_last_name",
      label: "Debtor Last Name",
      type: "string",
    },
    {
      key: "debtor_full_name",
      label: "Debtor Full Name",
      type: "string",
    },
    {
      key: "debtor_ssn",
      label: "Debtor SSN",
      type: "string",
    },
    {
      key: "debtor_dob",
      label: "Debtor Date Of Birth",
      type: DROMO_DATE_FORMAT,
    },
    {
      key: "debtor_drivers_license_number",
      label: "Debtor Driver's License Number",
      type: "string",
    },
    {
      key: "debtor_is_consent_for_text",
      label: "Debtor Consent for Text",
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: "debtor_is_consent_for_email",
      label: "Debtor Consent for Email",
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: "debtor_is_consent_for_ai_calls",
      label: "Debtor Consent for AI Calls",
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: "debtor_notes",
      label: "Debtor Notes",
      type: "string",
    },
  ];
  if (requiredFields) {
    fields
      .filter((field) => requiredFields.includes(field.key))
      .forEach((field) => {
        field.validators = [{ validate: "required" }];
      });
  }
  return fields;
}

export const debtorNotesField = {
  key: "debtor_notes",
  label: "Debtor Notes",
  type: "string",
};

export function getCodebtorEmailFields(debtorNumber) {
  const fields = [
    {
      key: getDebtorFieldKey("debtor_email_1_address", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 1] Address`,
      type: "email",
      validators: [
        {
          validate: "require_with",
          fields: [
            getDebtorFieldKey("debtor_email_1_status", debtorNumber),
            getDebtorFieldKey("debtor_email_1_consent_date", debtorNumber),
          ],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_email_1_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 1] Type`,
      type: "select",
      selectOptions: emailType,
    },
    {
      key: getDebtorFieldKey("debtor_email_1_status", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 1] Status`,
      type: "select",
      selectOptions: emailStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_email_1_consent_status", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 1] Consent Status`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_email_1_consent_date", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 1] Consent Date`,
      type: DROMO_DATE_FORMAT,
    },
    {
      key: getDebtorFieldKey("debtor_email_1_is_primary", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 1] Is Primary`,
      type: "checkbox",
    },
    {
      key: getDebtorFieldKey("debtor_email_1_notes", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 1] Notes`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_email_1_data_source", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 1] Source`,
      type: "select",
      selectOptions: dataSourceType,
    },
    {
      key: getDebtorFieldKey("debtor_email_2_address", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 2] Address`,
      type: "email",
      validators: [
        {
          validate: "require_with",
          fields: [
            getDebtorFieldKey("debtor_email_2_status", debtorNumber),
            getDebtorFieldKey("debtor_email_2_consent_date", debtorNumber),
          ],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_email_2_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 2] Type`,
      type: "select",
      selectOptions: emailType,
    },
    {
      key: getDebtorFieldKey("debtor_email_2_status", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 2] Status`,
      type: "select",
      selectOptions: emailStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_email_2_consent_status", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 2] Consent Status`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_email_2_consent_date", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 2] Consent Date`,
      type: DROMO_DATE_FORMAT,
    },
    {
      key: getDebtorFieldKey("debtor_email_2_is_primary", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 2] Is Primary`,
      type: "checkbox",
    },
    {
      key: getDebtorFieldKey("debtor_email_2_notes", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 2] Notes`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_email_2_data_source", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 2] Source`,
      type: "select",
      selectOptions: dataSourceType,
    },
    {
      key: getDebtorFieldKey("debtor_email_3_address", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 3] Address`,
      type: "email",
      validators: [
        {
          validate: "require_with",
          fields: [
            getDebtorFieldKey("debtor_email_3_status", debtorNumber),
            getDebtorFieldKey("debtor_email_3_consent_date", debtorNumber),
          ],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_email_3_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 3] Type`,
      type: "select",
      selectOptions: emailType,
    },
    {
      key: getDebtorFieldKey("debtor_email_3_status", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 3] Status`,
      type: "select",
      selectOptions: emailStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_email_3_consent_status", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 3] Consent Status`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_email_3_consent_date", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 3] Consent Date`,
      type: DROMO_DATE_FORMAT,
    },
    {
      key: getDebtorFieldKey("debtor_email_3_is_primary", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 3] Is Primary`,
      type: "checkbox",
    },
    {
      key: getDebtorFieldKey("debtor_email_3_notes", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 3] Notes`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_email_3_data_source", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Email 3] Source`,
      type: "select",
      selectOptions: dataSourceType,
    },
  ];
  return fields;
}

export function getCodebtorPhoneFields(debtorNumber) {
  const fields = [
    {
      key: getDebtorFieldKey("debtor_phone_number_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 1] Number`,
      type: "string",
      validators: [
        {
          validate: "require_with",
          fields: [
            getDebtorFieldKey("debtor_phone_type_1", debtorNumber),
            getDebtorFieldKey("debtor_phone_ai_permission_to_call_status_1", debtorNumber),
            getDebtorFieldKey("debtor_phone_permission_to_call_status_1", debtorNumber),
            getDebtorFieldKey("debtor_phone_permission_to_text_status_1", debtorNumber),
            getDebtorFieldKey("debtor_phone_status_1", debtorNumber),
          ],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_phone_type_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 1] Type`,
      type: "select",
      selectOptions: phoneType,
      validators: [
        {
          validate: "require_with",
          fields: [getDebtorFieldKey("debtor_phone_number_1", debtorNumber)],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_phone_is_primary_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 1] Is Primary`,
      type: "checkbox",
    },
    {
      key: getDebtorFieldKey("debtor_phone_ai_permission_to_call_status_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 1] AI Permission to Call Status`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_phone_permission_to_call_status_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 1] Permission to Call Status`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_phone_permission_to_text_status_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 1] Permission to Text Status`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_phone_status_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 1] Status`,
      type: "select",
      selectOptions: phoneStatus,
    },
    {
      key: getDebtorFieldKey("debtor_phone_extension_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 1] Extension`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_phone_notes_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 1] Notes`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_phone_data_source_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 1] Source`,
      type: "select",
      selectOptions: dataSourceType,
    },
    {
      key: getDebtorFieldKey("debtor_phone_number_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 2] Number`,
      type: "string",
      validators: [
        {
          validate: "require_with",
          fields: [
            getDebtorFieldKey("debtor_phone_type_2", debtorNumber),
            getDebtorFieldKey("debtor_phone_ai_permission_to_call_status_2", debtorNumber),
            getDebtorFieldKey("debtor_phone_permission_to_call_status_2", debtorNumber),
            getDebtorFieldKey("debtor_phone_permission_to_text_status_2", debtorNumber),
            getDebtorFieldKey("debtor_phone_status_2", debtorNumber),
          ],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_phone_type_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 2] Type`,
      type: "select",
      selectOptions: phoneType,
      validators: [
        {
          validate: "require_with",
          fields: [getDebtorFieldKey("debtor_phone_number_2", debtorNumber)],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_phone_is_primary_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 2] Is Primary`,
      type: "checkbox",
    },
    {
      key: getDebtorFieldKey("debtor_phone_ai_permission_to_call_status_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 2] AI Permission to Call Status`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_phone_permission_to_call_status_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 2] Permission to Call Status`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_phone_permission_to_text_status_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 2] Permission to Text Status`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_phone_status_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 2] Status`,
      type: "select",
      selectOptions: phoneStatus,
    },
    {
      key: getDebtorFieldKey("debtor_phone_extension_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 2] Extension`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_phone_notes_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 2] Notes`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_phone_data_source_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 2] Source`,
      type: "select",
      selectOptions: dataSourceType,
    },
    {
      key: getDebtorFieldKey("debtor_phone_number_3", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 3] Number`,
      type: "string",
      validators: [
        {
          validate: "require_with",
          fields: [
            getDebtorFieldKey("debtor_phone_type_3", debtorNumber),
            getDebtorFieldKey("debtor_phone_ai_permission_to_call_status_3", debtorNumber),
            getDebtorFieldKey("debtor_phone_permission_to_call_status_3", debtorNumber),
            getDebtorFieldKey("debtor_phone_permission_to_text_status_3", debtorNumber),
            getDebtorFieldKey("debtor_phone_status_3", debtorNumber),
          ],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_phone_type_3", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 3] Type`,
      type: "select",
      selectOptions: phoneType,
      validators: [
        {
          validate: "require_with",
          fields: [getDebtorFieldKey("debtor_phone_number_3", debtorNumber)],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_phone_is_primary_3", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 3] Is Primary`,
      type: "checkbox",
    },
    {
      key: getDebtorFieldKey("debtor_phone_ai_permission_to_call_status_3", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 3] AI Permission to Call Status`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_phone_permission_to_call_status_3", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 3] Permission to Call Status`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_phone_permission_to_text_status_3", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 3] Permission to Text Status`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_phone_status_3", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 3] Status`,
      type: "select",
      selectOptions: phoneStatus,
    },
    {
      key: getDebtorFieldKey("debtor_phone_extension_3", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 3] Extension`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_phone_notes_3", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 3] Notes`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_phone_data_source_3", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Phone 3] Source`,
      type: "select",
      selectOptions: dataSourceType,
    },
  ];
  return fields;
}

export function getCodebtorEmployerFields(debtorNumber) {
  const fields = [
    {
      key: getDebtorFieldKey("debtor_employer_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Name`,
      type: "string",
      validators: [
        {
          validate: "require_with",
          fields: [
            getDebtorFieldKey("debtor_employer_address_1", debtorNumber),
            getDebtorFieldKey("debtor_employer_address_2", debtorNumber),
            getDebtorFieldKey("debtor_employer_address_city", debtorNumber),
            getDebtorFieldKey("debtor_employer_address_state", debtorNumber),
            getDebtorFieldKey("debtor_employer_address_zip_code", debtorNumber),
            getDebtorFieldKey("debtor_employer_phone_number", debtorNumber),
            getDebtorFieldKey("debtor_employer_job_title", debtorNumber),
            getDebtorFieldKey("debtor_employer_salary", debtorNumber),
            getDebtorFieldKey("debtor_employer_email_address", debtorNumber),
            getDebtorFieldKey("debtor_employer_start_date", debtorNumber),
            getDebtorFieldKey("debtor_employer_end_date", debtorNumber),
            getDebtorFieldKey("debtor_employer_employment_type", debtorNumber),
            getDebtorFieldKey("debtor_employer_notes", debtorNumber),
          ],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_employer_address_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Address Line 1`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_employer_address_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Address Line 2`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_employer_address_city", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Address City`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_employer_address_state", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Address State`,
      type: "select",
      selectOptions: usStates,
    },
    {
      key: getDebtorFieldKey("debtor_employer_address_zip_code", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Address ZIP Code`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_employer_phone_number", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Phone Number`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_employer_is_primary", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Is Primary`,
      type: "checkbox",
    },
    {
      key: getDebtorFieldKey("debtor_employer_job_title", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Job Title`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_employer_salary", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Salary`,
      type: "number",
    },
    {
      key: getDebtorFieldKey("debtor_employer_email_address", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Email`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_employer_start_date", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Start Date`,
      type: DROMO_DATE_FORMAT,
    },
    {
      key: getDebtorFieldKey("debtor_employer_end_date", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] End Date`,
      type: DROMO_DATE_FORMAT,
    },
    {
      key: getDebtorFieldKey("debtor_employer_employment_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Employment Type`,
      type: "select",
      selectOptions: employmentTypeOptions,
    },
    {
      key: getDebtorFieldKey("debtor_employer_notes", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Notes`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_employer_data_source", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Employer] Source`,
      type: "select",
      selectOptions: dataSourceType,
    },
  ];
  return fields;
}

function getCommercialDebtorContactFields(debtorNumber) {
  const fields = [
    {
      key: getDebtorFieldKey("debtor_contact_1_relationship", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 1] Relationship`,
      type: "select",
      selectOptions: commercialContactRelationType,
    },
    {
      key: getDebtorFieldKey("debtor_contact_2_relationship", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 2] Relationship`,
      type: "select",
      selectOptions: commercialContactRelationType,
    },
    {
      key: getDebtorFieldKey("debtor_contact_3_relationship", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 3] Relationship`,
      type: "select",
      selectOptions: commercialContactRelationType,
    },
  ];
  return fields;
}

function getConsumerDebtorContactFields(debtorNumber) {
  const fields = [
    {
      key: getDebtorFieldKey("debtor_contact_1_relationship", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 1] Relationship`,
      type: "select",
      selectOptions: consumerContactRelationType,
    },
    {
      key: getDebtorFieldKey("debtor_contact_2_relationship", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 2] Relationship`,
      type: "select",
      selectOptions: consumerContactRelationType,
    },
    {
      key: getDebtorFieldKey("debtor_contact_3_relationship", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 3] Relationship`,
      type: "select",
      selectOptions: consumerContactRelationType,
    },
  ];
  return fields;
}

export function getCodebtorContactFields(debtorNumber, accountType) {
  return [
    {
      key: getDebtorFieldKey("debtor_contact_1_first_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 1] First Name`,
      type: "string",
      validators: [
        {
          validate: "require_with",
          fields: [
            getDebtorFieldKey("debtor_contact_1_middle_name", debtorNumber),
            getDebtorFieldKey("debtor_contact_1_email", debtorNumber),
            getDebtorFieldKey("debtor_contact_1_email_type", debtorNumber),
            getDebtorFieldKey("debtor_contact_1_phone_1_number", debtorNumber),
            getDebtorFieldKey("debtor_contact_1_phone_1_type", debtorNumber),
            getDebtorFieldKey("debtor_contact_1_phone_2_number", debtorNumber),
            getDebtorFieldKey("debtor_contact_1_phone_2_type", debtorNumber),
          ],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_contact_1_middle_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 1] Middle Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_1_last_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 1] Last Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_1_full_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 1] Full Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_1_email", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 1] Email`,
      type: "email",
    },
    {
      key: getDebtorFieldKey("debtor_contact_1_email_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 1] Email Type`,
      type: "select",
      selectOptions: emailType,
    },
    {
      key: getDebtorFieldKey("debtor_contact_1_phone_1_number", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 1] Phone 1 Number`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_1_phone_1_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 1] Phone 1 Type`,
      type: "select",
      selectOptions: phoneType,
    },
    {
      key: getDebtorFieldKey("debtor_contact_1_phone_2_number", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 1] Phone 2 Number`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_1_phone_2_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 1] Phone 2 Type`,
      type: "select",
      selectOptions: phoneType,
    },
    {
      key: getDebtorFieldKey("debtor_contact_2_first_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 2] First Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_2_middle_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 2] Middle Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_2_last_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 2] Last Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_2_full_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 2] Full Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_2_email", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 2] Email`,
      type: "email",
    },
    {
      key: getDebtorFieldKey("debtor_contact_2_email_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 2] Email Type`,
      type: "select",
      selectOptions: emailType,
    },
    {
      key: getDebtorFieldKey("debtor_contact_2_phone_1_number", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 2] Phone 1 Number`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_2_phone_1_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 2] Phone 1 Type`,
      type: "select",
      selectOptions: phoneType,
    },
    {
      key: getDebtorFieldKey("debtor_contact_2_phone_2_number", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 2] Phone 2 Number`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_2_phone_2_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 2] Phone 2 Type`,
      type: "select",
      selectOptions: phoneType,
    },
    {
      key: getDebtorFieldKey("debtor_contact_3_first_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 3] First Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_3_middle_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 3] Middle Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_3_last_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 3] Last Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_3_full_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 3] Full Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_3_email", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 3] Email`,
      type: "email",
    },
    {
      key: getDebtorFieldKey("debtor_contact_3_email_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 3] Email Type`,
      type: "select",
      selectOptions: emailType,
    },
    {
      key: getDebtorFieldKey("debtor_contact_3_phone_1_number", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 3] Phone 1 Number`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_3_phone_1_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 3] Phone 1 Type`,
      type: "select",
      selectOptions: phoneType,
    },
    {
      key: getDebtorFieldKey("debtor_contact_3_phone_2_number", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 3] Phone 2 Number`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_contact_3_phone_2_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Contact 3] Phone 2 Type`,
      type: "select",
      selectOptions: phoneType,
    },
    ...(accountType === "commercial"
      ? getCommercialDebtorContactFields(debtorNumber)
      : getConsumerDebtorContactFields(debtorNumber)),
  ];
}

export function getCodebtorAddressFields(debtorNumber, requiredFields) {
  const fields = [
    {
      key: getDebtorFieldKey("debtor_address_is_primary", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 1] Is Primary`,
      type: "checkbox",
    },
    {
      key: getDebtorFieldKey("debtor_address_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 1] Line 1`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 1] Line 2`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_city", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 1] City`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_state", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 1] State`,
      type: "select",
      selectOptions: usStates,
    },
    {
      key: getDebtorFieldKey("debtor_address_zip_code", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 1] ZIP Code`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 1] Type`,
      type: "select",
      selectOptions: addressType,
    },
    {
      key: getDebtorFieldKey("debtor_address_notes", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 1] Notes`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_data_source", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 1] Source`,
      type: "select",
      selectOptions: dataSourceType,
    },
    {
      key: getDebtorFieldKey("debtor_address_2_is_primary", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 2] Is Primary`,
      type: "checkbox",
    },
    {
      key: getDebtorFieldKey("debtor_address_2_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 2] Line 1`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_2_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 2] Line 2`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_2_city", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 2] City`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_2_state", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 2] State`,
      type: "select",
      selectOptions: usStates,
    },
    {
      key: getDebtorFieldKey("debtor_address_2_zip_code", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 2] ZIP Code`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_2_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 2] Type`,
      type: "select",
      selectOptions: addressType,
    },
    {
      key: getDebtorFieldKey("debtor_address_2_notes", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 2] Notes`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_2_data_source", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 2] Source`,
      type: "select",
      selectOptions: dataSourceType,
    },
    {
      key: getDebtorFieldKey("debtor_address_3_is_primary", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 3] Is Primary`,
      type: "checkbox",
    },
    {
      key: getDebtorFieldKey("debtor_address_3_1", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 3] Line 1`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_3_2", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 3] Line 2`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_3_city", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 3] City`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_3_state", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 3] State`,
      type: "select",
      selectOptions: usStates,
    },
    {
      key: getDebtorFieldKey("debtor_address_3_zip_code", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 3] ZIP Code`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_3_type", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 3] Type`,
      type: "select",
      selectOptions: addressType,
    },
    {
      key: getDebtorFieldKey("debtor_address_3_notes", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 3] Notes`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_address_3_data_source", debtorNumber),
      label: `[Debtor ${debtorNumber}] [Address 3] Source`,
      type: "select",
      selectOptions: dataSourceType,
    },
  ];

  if (requiredFields) {
    fields
      .filter((field) => requiredFields.includes(field.key))
      .forEach((field) => {
        field.validators = [...(field.validators || []), { validate: "required", fields: [] }];
      });
  }

  return fields;
}

export function getCodebtorConsumerFields(debtorNumber, requiredFields, requiredWithFields) {
  const fields = [
    {
      key: getDebtorFieldKey("debtor_first_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] First Name`,
      type: "string",
      validators: [
        {
          validate: "require_with",
          fields: [getDebtorFieldKey("debtor_last_name", debtorNumber)],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_middle_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] Middle Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_last_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] Last Name`,
      type: "string",
      validators: [
        {
          validate: "require_with",
          fields: [getDebtorFieldKey("debtor_first_name", debtorNumber)],
        },
      ],
    },
    {
      key: getDebtorFieldKey("debtor_full_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] Full Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_ssn", debtorNumber),
      label: `[Debtor ${debtorNumber}] SSN`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_dob", debtorNumber),
      label: `[Debtor ${debtorNumber}] Date Of Birth`,
      type: DROMO_DATE_FORMAT,
    },
    {
      key: getDebtorFieldKey("debtor_drivers_license_number", debtorNumber),
      label: `[Debtor ${debtorNumber}] Driver's License Number`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_is_consent_for_text", debtorNumber),
      label: `[Debtor ${debtorNumber}] Consent for Text`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_is_consent_for_email", debtorNumber),
      label: `[Debtor ${debtorNumber}] Consent for Email`,
      type: "select",
      selectOptions: consentStatusType,
    },
    {
      key: getDebtorFieldKey("debtor_is_consent_for_ai_calls", debtorNumber),
      label: `[Debtor ${debtorNumber}] Consent for AI Calls`,
      type: "select",
      selectOptions: consentStatusType,
    },
  ];

  const allRequiredWithFields = [...requiredWithFields, ...fields.map((field) => field.key)];

  if (requiredFields) {
    fields
      .filter((field) => requiredFields.includes(field.key))
      .forEach((field) => {
        field.validators = [...(field.validators || []), { validate: "required", fields: [] }];
      });
    const transformedRequiredFields = requiredFields.map((field) =>
      getDebtorFieldKey(field, debtorNumber),
    );
    fields
      .filter((field) => transformedRequiredFields.includes(field.key))
      .forEach((field) => {
        if (!field.validators) {
          field.validators = [];
        }
        field.validators.push({ validate: "require_with", fields: allRequiredWithFields });
      });
  }

  return fields;
}

export function getCodebtorCommercialFields(debtorNumber, requiredFields, requiredWithFields) {
  const fields = [
    {
      key: getDebtorFieldKey("debtor_company_name", debtorNumber),
      label: `[Debtor ${debtorNumber}] Company Name`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_ein", debtorNumber),
      label: `[Debtor ${debtorNumber}] Company EIN`,
      type: "string",
    },
  ];

  if (requiredFields) {
    fields
      .filter((field) => requiredFields.includes(field.key))
      .forEach((field) => {
        field.validators = [{ validate: "required" }];
      });
    const transformedRequiredFields = requiredFields.map((field) =>
      getDebtorFieldKey(field, debtorNumber),
    );
    fields
      .filter((field) => transformedRequiredFields.includes(field.key))
      .forEach((field) => {
        if (!field.validators) {
          field.validators = [];
        }
        field.validators.push({ validate: "require_with", fields: requiredWithFields });
      });
  }

  return fields;
}

export function getCodebtorNotesFields(debtorNumber) {
  return [
    {
      key: getDebtorFieldKey("debtor_notes", debtorNumber),
      label: `[Debtor ${debtorNumber}] Notes`,
      type: "string",
    },
  ];
}

export function getCodebtorMilitaryFields(debtorNumber) {
  return [
    {
      key: getDebtorFieldKey("debtor_military_status", debtorNumber),
      label: `[Debtor ${debtorNumber}] Military Status`,
      type: "select",
      selectOptions: militaryStatusOptions,
    },
    {
      key: getDebtorFieldKey("debtor_military_service_branch", debtorNumber),
      label: `[Debtor ${debtorNumber}] Military Service Branch`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_military_active_duty_start_date", debtorNumber),
      label: `[Debtor ${debtorNumber}] Military Active Duty Start Date`,
      type: DROMO_DATE_FORMAT,
    },
    {
      key: getDebtorFieldKey("debtor_military_active_duty_end_date", debtorNumber),
      label: `[Debtor ${debtorNumber}] Military Active Duty End Date`,
      type: DROMO_DATE_FORMAT,
    },
    {
      key: getDebtorFieldKey("debtor_military_scrub_date", debtorNumber),
      label: `[Debtor ${debtorNumber}] Military Verification Date`,
      type: DROMO_DATE_FORMAT,
    },
    {
      key: getDebtorFieldKey("debtor_military_certificate_id", debtorNumber),
      label: `[Debtor ${debtorNumber}] Military SCRA Certificate ID`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_military_notes", debtorNumber),
      label: `[Debtor ${debtorNumber}] Military Notes`,
      type: "string",
    },
  ];
}

export function getCodebtorBankruptcyFields(debtorNumber) {
  return [
    {
      key: getDebtorFieldKey("debtor_bankruptcy_case_number", debtorNumber),
      label: `[Debtor ${debtorNumber}] Bankruptcy Case Number`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_bankruptcy_chapter", debtorNumber),
      label: `[Debtor ${debtorNumber}] Bankruptcy Chapter`,
      type: "select",
      selectOptions: bankruptcyChapterOptions,
    },
    {
      key: getDebtorFieldKey("debtor_bankruptcy_filing_status", debtorNumber),
      label: `[Debtor ${debtorNumber}] Bankruptcy Filing Status`,
      type: "string",
    },
    {
      key: getDebtorFieldKey("debtor_bankruptcy_filing_date", debtorNumber),
      label: `[Debtor ${debtorNumber}] Bankruptcy Filing Date`,
      type: DROMO_DATE_FORMAT,
    },
    {
      key: getDebtorFieldKey("debtor_bankruptcy_dismissal_date", debtorNumber),
      label: `[Debtor ${debtorNumber}] Bankruptcy Dismissal Date`,
      type: DROMO_DATE_FORMAT,
    },
    {
      key: getDebtorFieldKey("debtor_bankruptcy_discharge_date", debtorNumber),
      label: `[Debtor ${debtorNumber}] Bankruptcy Discharge Date`,
      type: DROMO_DATE_FORMAT,
    },
    {
      key: getDebtorFieldKey("debtor_bankruptcy_notes", debtorNumber),
      label: `[Debtor ${debtorNumber}] Bankruptcy Notes`,
      type: "string",
    },
  ];
}

export function getDebtorsDataFields(numberOfDebtors, debtorType) {
  const debtorDataFields = [];
  if (debtorType !== "consumer" && debtorType !== "commercial") {
    return debtorDataFields;
  }

  for (let i = 1; i <= numberOfDebtors; i++) {
    const debtorFields = [
      ...getCodebtorAddressFields(i, [
        "debtor_address_1",
        "debtor_address_city",
        "debtor_address_state",
        "debtor_address_zip_code",
        "debtor_address_type",
      ]),

      ...getCodebtorEmailFields(i),
      ...getCodebtorPhoneFields(i),
      ...getCodebtorEmployerFields(i),
      ...getCodebtorContactFields(i, debtorType),
      ...getCodebtorMilitaryFields(i),
      ...getCodebtorBankruptcyFields(i),
      ...getCodebtorNotesFields(i),
    ];

    const requiredWithFields = debtorFields.map((field) => field.key);

    if (debtorType === "commercial") {
      debtorFields.push(
        ...getCodebtorCommercialFields(i, ["debtor_company_name"], requiredWithFields),
      );
    } else {
      debtorFields.push(...getCodebtorConsumerFields(i, [], requiredWithFields));
    }

    debtorDataFields.push(...debtorFields);
  }

  return debtorDataFields;
}

/**
 * Transforms debtor custom fields schema to support multiple debtors
 * by appending debtor number to label and key.
 *
 * @param {Array} debtorCustomFieldsSchema - Original schema of custom fields
 * @param {number} debtorNumber - The debtor number to append
 * @returns {Array} Transformed schema with debtor number in keys and labels
 *
 * @example
 * // Input:
 * // [
 * //   {
 * //     label: "Custom field 1",
 * //     key: "custom_field_1",
 * //   }
 * // ]
 * // Output:
 * // [
 * //   {
 * //     label: "[Debtor 1] Custom field 1",
 * //     key: "debtor_1_custom_field_1",
 * //   }
 * // ]
 */
export function getCodebtorCustomFields(debtorCustomFieldsSchema, debtorNumber) {
  return debtorCustomFieldsSchema.map((field) => ({
    label: `[Debtor ${debtorNumber}] ${field.label}`,
    key: getDebtorFieldKey(field.key, debtorNumber),
  }));
}

export function getCodebtorCustomFieldsSchema(debtorCustomFieldsSchema, numberOfDebtors) {
  /**
   * Transforms debtor custom fields schema to support multiple debtors
   * in the same import file by creating field schemas for each debtor.
   *
   * @param {Array} debtorCustomFieldsSchema - Original schema of custom fields
   * @param {number} numberOfDebtors - Number of debtors to generate fields for
   * @returns {Array} List of custom field schemas for all debtors
   *
   * @example
   * // Input:
   * // [
   * //   {
   * //     label: "Custom field 1",
   * //     key: "custom_field_1",
   * //   },
   * //   {
   * //     label: "Custom field 2",
   * //     key: "custom_field_2",
   * //   }
   * // ]
   * // Output:
   * // [
   * //   {
   * //     label: "[Debtor 1] Custom field 1",
   * //     key: "debtor_1_custom_field_1",
   * //   },
   * //   {
   * //     label: "[Debtor 2] Custom field 1",
   * //     key: "debtor_2_custom_field_1",
   * //   },
   * //   {
   * //     label: "[Debtor 3] Custom field 1",
   * //     key: "debtor_3_custom_field_1",
   * //   }
   * // ]
   */
  const codebtorCustomFieldsSchema = [];

  for (let i = 1; i <= numberOfDebtors; i++) {
    codebtorCustomFieldsSchema.push(...getCodebtorCustomFields(debtorCustomFieldsSchema, i));
  }

  return codebtorCustomFieldsSchema;
}
